import { useCallback } from 'react';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { requestBuilder } from '../../http/RequestBuilder';
import { captureException } from '../../logger/Logger';
import { useUser } from '../../user/providers/UserProvider';
import type { MemberJourneyLog } from '../../user/types/User';

const mutateMemberJourneyLog = requestBuilder.mutation<
    unknown,
    MemberJourneyLog
>({
    endpoint: '/api/member-journey/log',
    method: 'PATCH',
});

export const useUpdateMemberJourneyLog = () => {
    const user = useAuthedUser();
    const userId = user.id;
    const { refetch: refetchUser, setOptimisticUserData } = useUser();
    const { mutateAsync, isPending } = mutateMemberJourneyLog({
        onMutate: ({ body: memberJourneyLogUpdates }) => {
            setOptimisticUserData?.((prevUser) => ({
                ...prevUser,
                memberJourneyLog: {
                    ...prevUser.memberJourneyLog,
                    ...memberJourneyLogUpdates,
                },
            }));
        },
        onError: (error) => {
            captureException(error);
        },
        onSettled: async () => {
            await refetchUser?.();
        },
    });

    const updateMemberJourneyLog = useCallback(
        async (memberJourneyLog: MemberJourneyLog) => {
            await mutateAsync({
                urlParams: { userId },
                body: memberJourneyLog,
            });
        },
        [mutateAsync, userId],
    );

    return {
        updateMemberJourneyLog,
        isPending,
    };
};
